import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import {
  getCronJobs,
  getCronJobById,
  createCronJob,
  updateCronJob,
  deleteCronJob,
  updateCronJobStatus,
  getCronLogs, // Now accepts a cronId parameter
} from '../../services/cronService';

// Fetch all cron jobs
export const fetchCronJobs = createAsyncThunk('cron/fetchCronJobs', async () => {
  const response = await getCronJobs();
  return response;
});

// Fetch a single cron job by ID
export const fetchCronJobById = createAsyncThunk('cron/fetchCronJobById', async (id) => {
  const response = await getCronJobById(id);
  return response;
});

// Delete multiple cron jobs
export const deleteCronJobs = createAsyncThunk('cron/deleteCronJobs', async (ids) => {
  await Promise.all(ids.map((id) => deleteCronJob(id)));
  return ids;
});

// Update one or more cron jobs
export const updateCronJobs = createAsyncThunk(
  'cron/updateCronJobs',
  async ({ ids, cronJob }) => {
    await Promise.all(ids.map((id) => updateCronJob({ ...cronJob, id })));
    return { ids, cronJob };
  }
);

// Update status for one or more cron jobs
export const updateCronJobsStatus = createAsyncThunk(
  'cron/updateCronJobsStatus',
  async ({ ids, status }) => {
    await Promise.all(ids.map((id) => updateCronJobStatus({ ids, status })));
    return { ids, status };
  }
);

// Create a new cron job
export const createCronJobAction = createAsyncThunk('cron/createCronJob', async (cronJob) => {
  const response = await createCronJob(cronJob);
  return response;
});

// ===========================
// Updated: Fetch cron logs by cron job ID
// ===========================
export const fetchCronLogs = createAsyncThunk('cron/fetchCronLogs', async (cronId) => {
  const response = await getCronLogs(cronId);
  return response;
});

const initialState = {
  cronJobs: [],
  cronJob: null,
  status: 'idle',
  cronLogs: [], // Holds logs for a specific cron job (fetched by ID)
  loading: false,
  error: null,
};

const cronSlice = createSlice({
  name: 'cron',
  initialState,
  reducers: {
    clearCronJob: (state) => {
      state.cronJob = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch all cron jobs
      .addCase(fetchCronJobs.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchCronJobs.fulfilled, (state, action) => {
        state.cronJobs = action.payload;
        state.loading = false;
      })
      .addCase(fetchCronJobs.rejected, (state, action) => {
        state.error = action.error.message;
        state.loading = false;
      })
      // Fetch single cron job by ID
      .addCase(fetchCronJobById.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchCronJobById.fulfilled, (state, action) => {
        state.cronJob = action.payload;
        state.loading = false;
      })
      .addCase(fetchCronJobById.rejected, (state, action) => {
        state.error = action.error.message;
        state.loading = false;
      })
      // Delete cron jobs
      .addCase(deleteCronJobs.fulfilled, (state, action) => {
        state.cronJobs = state.cronJobs.filter(
          (cronJob) => !action.payload.includes(cronJob.id)
        );
      })
      // Update cron jobs
      .addCase(updateCronJobs.fulfilled, (state, action) => {
        state.cronJobs = state.cronJobs.map((cronJob) =>
          action.payload.ids.includes(cronJob.id)
            ? { ...cronJob, ...action.payload.cronJob }
            : cronJob
        );
      })
      // Update cron job status
      .addCase(updateCronJobsStatus.fulfilled, (state, action) => {
        const { ids, status } = action.payload;
        state.cronJobs = state.cronJobs.map((cronJob) =>
          ids.includes(cronJob.id) ? { ...cronJob, status } : cronJob
        );
      })
      // Create a new cron job
      .addCase(createCronJobAction.fulfilled, (state, action) => {
        state.cronJobs.push(action.payload);
      })
      // Fetch cron logs for a specific cron job
      .addCase(fetchCronLogs.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchCronLogs.fulfilled, (state, action) => {
        state.cronLogs = action.payload;
        state.loading = false;
      })
      .addCase(fetchCronLogs.rejected, (state, action) => {
        state.error = action.error.message;
        state.loading = false;
      });
  },
});

export const { clearCronJob } = cronSlice.actions;
export default cronSlice.reducer;
